import * as React from 'react';
import { TemplateBrowser } from '../Components/Templates/TemplateBrowser';
import { TemplateEditor } from '../Components/Templates/TemplateEditor';
import { TemplateSelectorModal } from '../Components/Templates/TemplateSelectorModal';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import type { UserLetter } from '../../MedicareProAPI/Entities/User/UserLetter';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import { UserLetterRepository } from '../../MedicareProAPI/Repositories/User/UserLetterRepository';
import type { GlobUserTemplate } from '../../MedicareProAPI/Entities/Glob/GlobUserTemplate';
import type { TemplateType } from '../Constants/AgentReportingTemplates';
import { TEMPLATE_TYPES } from '../Constants/AgentReportingTemplates';

interface TemplateManagerPanelProps {
  userAccessToken: string;
  userId: number;
  agencyId: number | null;
  uplineId: number | null;
  absoluteUplineId: number | null;
  renderedExternal?: boolean;
}

/**
 * Panel for managing Templates
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-05-20
 * @lastUpdated
 * @version 1.1.0
 */
export default function TemplateManagerPanel({
  userId,
  userAccessToken,
  agencyId,
  uplineId,
  absoluteUplineId,
  renderedExternal = false,
}: TemplateManagerPanelProps): React.JSX.Element {
  const [user, setUser] = React.useState<User>(null);
  const [letters, setLetters] = React.useState<any>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [mergeTags, setMergeTags] = React.useState<any>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<UserLetter>(null);
  const [folderOptions, setFolderOptions] = React.useState<string[]>([]);

  const [showTemplateModal, setShowTemplateModal] = React.useState<boolean>(false);

  const [blankTemplateSelected, setBlankTemplateSelected] = React.useState<boolean>(false);
  const [starterTemplateSelected, setStarterTemplateSelected] =
    React.useState<GlobUserTemplate>(null);
  const [sharedTemplateSelected, setSharedTemplateSelected] = React.useState<UserLetter>(null);

  /**
   * Fetches the necessary user and letter data for the panel and its child components.
   *
   * - Retrieves user details (including client portals)
   * - Retrieves user's grouped letters
   * - Generates merge tags based on user data
   * - Sets loading and folder state
   */
  const fetchData = async () => {
    setLoading(true);
    const userRepository = new UserRepository();
    const userLetterRepository = new UserLetterRepository();

    userRepository.setAccessToken(userAccessToken);
    userLetterRepository.setAccessToken(userAccessToken);

    const user = await userRepository.getUser(userId, { include: ['clientPortals'] });
    const letters = await userLetterRepository.getUserLettersGrouped(userId);

    setUser(user);
    setLetters(letters);
    setMergeTags(generateMergeTags(user)); // Placeholder function
    setLoading(false);
    if (letters) {
      setFolderOptions(Object.keys(letters));
    }
  };

  /**
   * Adds a new folder name to the list of folder options if it doesn't already exist.
   *
   * @param newFolder - The name of the new folder to add
   */
  const addFolderOption = (newFolder: string): void => {
    if (!folderOptions.includes(newFolder)) {
      setFolderOptions([...folderOptions, newFolder]);
    }
  };

  /**
   * Handles the selection of a template type (blank, starter, or shared),
   * updates the relevant state accordingly, and closes the template modal.
   *
   * @param type - The type of template selected: 'blank', 'starter', or 'shared'
   * @param payload - Optional data associated with 'starter' or 'shared' templates
   */
  const handleTemplateSelect = (type: TemplateType, payload?: any): void => {
    setBlankTemplateSelected(type === TEMPLATE_TYPES.BLANK);
    setStarterTemplateSelected(type === TEMPLATE_TYPES.STARTER ? payload : null);
    setSharedTemplateSelected(type === TEMPLATE_TYPES.SHARED ? payload : null);
    setSelectedTemplate(null);
    setShowTemplateModal(false);
  };

  React.useEffect(() => {
    fetchData();
  }, [userId, userAccessToken]);

  /**
   * Handler passed to child component on save/update of template
   */
  const handleSave = () => {
    fetchData();
  };

  /**
   * Handler passed to child component on template deletion
   */
  const handleDelete = () => {
    setBlankTemplateSelected(false);
    setStarterTemplateSelected(null);
    setSharedTemplateSelected(null);
    setSelectedTemplate(null);
    fetchData();
  };

  return (
    <>
      <div className="row contactCard">
        <div className="col-12  px-2  sm-resp">
          <div
            className="card cardAddress"
            style={{
              height: 'auto',
              boxShadow: renderedExternal ? 'none' : undefined,
            }}
          >
            <div className="card-body" id="rxCards">
              <div id="letterFormReturn" className="mt-3">
                <div className="row">
                  <div className="col-3 border-right">
                    {letters && (
                      <TemplateBrowser
                        letters={letters}
                        selectedTemplate={selectedTemplate}
                        onNewTemplate={() => setShowTemplateModal(true)}
                        onSelect={(template: UserLetter) => setSelectedTemplate(template)}
                      />
                    )}
                  </div>
                  <div className="col-9">
                    {
                      <TemplateEditor
                        userId={userId}
                        accessToken={userAccessToken}
                        template={selectedTemplate}
                        blankTemplate={blankTemplateSelected}
                        starterTemplate={starterTemplateSelected}
                        sharedTemplate={sharedTemplateSelected}
                        mergeTags={mergeTags}
                        agencyId={agencyId}
                        folderOptions={folderOptions}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onAddFolder={addFolderOption}
                      />
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showTemplateModal && (
        <TemplateSelectorModal
          userId={userId}
          userAccessToken={userAccessToken}
          open={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onTemplateSelect={handleTemplateSelect}
          uplineId={uplineId}
          absoluteUplineId={absoluteUplineId}
        />
      )}
    </>
  );
}

// Example stub for merge tag generation
type ClientPortal = { getPortalName: () => string; getId: () => number };
function generateMergeTags(user: User) {
  const portals: ClientPortal[] = user.getClientPortals()?.getItems?.() || [];

  const portalMergeList = [];
  const portalQRMergeList = [];

  if (portals.length > 0) {
    portals.forEach((portal) => {
      const obj = {
        value: 'CP-' + portal.getPortalName() + '-' + portal.getId(),
        title: 'Portal - ' + portal.getPortalName(),
      };
      portalMergeList.push(obj);
    });

    portals.forEach((portal) => {
      const obj = {
        value: 'CPQR-' + portal.getPortalName() + '-' + portal.getId(),
        title: 'Portal QR Code- ' + portal.getPortalName(),
      };
      portalQRMergeList.push(obj);
    });
  } else {
    var obj = { value: 'PORTAL', title: 'Portal Invite' };
    portalMergeList.push(obj);

    var obj = { value: 'QRCODE', title: 'Portal Invite (QR code)' };
    portalQRMergeList.push(obj);
  }

  return [
    { value: 'DATE', title: 'Date' },
    {
      title: 'Individual',
      menu: [
        { value: 'FULLNAME', title: 'Full Name' },
        { value: 'FIRSTNAME', title: 'First Name (nickname if any)' },
        { value: 'LASTNAME', title: 'Last Name' },
        { value: 'FIRST-STRICT', title: 'First Name only' },
        { value: 'PREFIX', title: 'Title' },
        { value: 'ADDRESS', title: 'Address' },
        { value: 'INDIVIDUAL-EMAIL', title: 'Email' },
        { value: 'INDIVIDUAL-PHONE', title: 'Phone' },
      ],
    },
    {
      title: 'Automation Specific Merge Tags',
      menu: [
        { value: 'UPSERT-POL-STATUS', title: 'New/Updated policy status' },
        { value: 'UPSERT-POL-COMPANY', title: 'New/Updated policy company' },
        { value: 'UPSERT-POL-PLAN', title: 'New/Updated policy plan name' },
        { value: 'UPSERT-POL-EFFECTIVE', title: 'New/Updated policy effective' },
        { value: 'UPSERT-POL-PREMIUM', title: 'New/Updated policy premium' },
        { value: 'UPSERT-POL-SUBSIDY', title: 'New/Updated policy subsidy' },
        { value: 'UPSERT-POL-ACC-VALUE', title: 'New/Updated policy premium/acc value' },
        { value: 'UPSERT-POL-ACC-VAL-DT', title: 'New/Updated policy premium/acc value date' },
        { value: 'UPSERT-POL-AOR-FRST', title: 'New/Updated policy AOR first name' },
        { value: 'UPSERT-POL-AOR-LST', title: 'New/Updated policy AOR last name' },
        { value: 'UPSERT-POL-AOR-EML', title: 'New/Updated policy AOR email' },
        { value: 'UPSERT-POL-AOR-PHN', title: 'New/Updated policy AOR phone' },
      ],
    },
    {
      title: 'Policy',
      menu: [
        {
          title: 'MAPD',
          menu: [
            { value: 'NEW-MAPD-COMPANY', title: 'Newest MAPD company name' },
            { value: 'NEW-MAPD-PLAN', title: 'Newest MAPD plan name' },
            { value: 'NEW-MAPD-EFFECTIVE', title: 'Newest MAPD effective' },
            { value: 'NEW-MAPD-NUMBER', title: 'Newest MAPD policy number' },
            { value: 'NEW-MAPD-BENEFITS', title: 'Newest MAPD plan benefits' },
            { value: 'NEW-MAPD-AOR-FRST', title: 'Newest MAPD AOR first name' },
            { value: 'NEW-MAPD-AOR-LST', title: 'Newest MAPD AOR last name' },
            { value: 'NEW-MAPD-AOR-EML', title: 'Newest MAPD AOR email' },
            { value: 'NEW-MAPD-AOR-PHN', title: 'Newest MAPD AOR phone' },
          ],
        },
        {
          title: 'PDP',
          menu: [
            { value: 'NEW-PDP-COMPANY', title: 'Newest PDP company name' },
            { value: 'NEW-PDP-PLAN', title: 'Newest PDP plan name' },
            { value: 'NEW-PDP-EFFECTIVE', title: 'Newest PDP effective' },
            { value: 'NEW-PDP-NUMBER', title: 'Newest PDP policy number' },
            { value: 'NEW-PDP-AOR-FRST', title: 'Newest PDP AOR first name' },
            { value: 'NEW-PDP-AOR-LST', title: 'Newest PDP AOR last name' },
            { value: 'NEW-PDP-AOR-EML', title: 'Newest PDP AOR email' },
            { value: 'NEW-PDP-AOR-PHN', title: 'Newest PDP AOR phone' },
            //{ value: 'NEW-PDP-BENEFITS', title: 'Newest PDP plan benefits' }
          ],
        },
        {
          title: 'Medicare Supplement',
          menu: [
            { value: 'NEW-SUP-COMPANY', title: 'Newest MedSup company name' },
            { value: 'NEW-SUP-PLAN', title: 'Newest MedSup plan name' },
            { value: 'NEW-SUP-EFFECTIVE', title: 'Newest MedSup effective' },
            { value: 'NEW-SUP-NUMBER', title: 'Newest MedSup policy number' },
            { value: 'NEW-SUP-PREMIUM', title: 'Newest MedSup premium' },
            { value: 'NEW-SUP-PREM-DT', title: 'Newest MedSup premium date' },
            { value: 'NEW-SUP-AOR-FRST', title: 'Newest SUP AOR first name' },
            { value: 'NEW-SUP-AOR-LST', title: 'Newest SUP AOR last name' },
            { value: 'NEW-SUP-AOR-EML', title: 'Newest SUP AOR email' },
            { value: 'NEW-SUP-AOR-PHN', title: 'Newest SUP AOR phone' },
          ],
        },
        {
          title: 'Individual Health/ACA',
          menu: [
            { value: 'NEW-IND-COMPANY', title: 'Newest Health company name' },
            { value: 'NEW-IND-PLAN', title: 'Newest Health plan name' },
            { value: 'NEW-IND-EFFECTIVE', title: 'Newest Health effective' },
            { value: 'NEW-IND-PREMIUM', title: 'Newest Health premium (member)' },
            { value: 'NEW-IND-PREM-TOT', title: 'Newest Health premium (total)' },
            { value: 'NEW-IND-SUBSIDY', title: 'Newest Health subsidy' },
            { value: 'NEW-IND-NUMBER', title: 'Newest Health policy number' },
            { value: 'NEW-IND-AOR-FRST', title: 'Newest IND AOR first name' },
            { value: 'NEW-IND-AOR-LST', title: 'Newest IND AOR last name' },
            { value: 'NEW-IND-AOR-EML', title: 'Newest IND AOR email' },
            { value: 'NEW-IND-AOR-PHN', title: 'Newest IND AOR phone' },
          ],
        },
        {
          title: 'Ancillary',
          menu: [
            { value: 'NEW-ANC-COMPANY', title: 'Newest Ancillary company name' },
            { value: 'NEW-ANC-PLAN', title: 'Newest Ancillary plan name' },
            { value: 'NEW-ANC-EFFECTIVE', title: 'Newest Ancillary effective' },
            { value: 'NEW-ANC-PREMIUM', title: 'Newest Ancillary premium' },
            { value: 'NEW-ANC-NUMBER', title: 'Newest Ancillary policy number' },
            { value: 'NEW-ANC-AOR-FRST', title: 'Newest ANC AOR first name' },
            { value: 'NEW-ANC-AOR-LST', title: 'Newest ANC AOR last name' },
            { value: 'NEW-ANC-AOR-EML', title: 'Newest ANC AOR email' },
            { value: 'NEW-ANC-AOR-PHN', title: 'Newest ANC AOR phone' },
          ],
        },
        {
          title: 'Life',
          menu: [
            { value: 'NEW-LIFE-COMPANY', title: 'Newest Life company name' },
            { value: 'NEW-LIFE-PLAN', title: 'Newest Life plan name' },
            { value: 'NEW-LIFE-EFFECTIVE', title: 'Newest Life effective' },
            { value: 'NEW-LIFE-PREMIUM', title: 'Newest Life premium' },
            { value: 'NEW-LIFE-NUMBER', title: 'Newest Life policy number' },
            { value: 'NEW-LIFE-DB', title: 'Newest Life death benefit' },
            { value: 'NEW-LIFE-AOR-FRST', title: 'Newest LIFE AOR first name' },
            { value: 'NEW-LIFE-AOR-LST', title: 'Newest LIFE AOR last name' },
            { value: 'NEW-LIFE-AOR-EML', title: 'Newest LIFE AOR email' },
            { value: 'NEW-LIFE-AOR-PHN', title: 'Newest LIFE AOR phone' },
          ],
        },
        {
          title: 'Annuity',
          menu: [
            { value: 'NEW-ANNUITY-COMPANY', title: 'Newest Annuity company name' },
            { value: 'NEW-ANNUITY-PLAN', title: 'Newest Annuity plan name' },
            { value: 'NEW-ANNUITY-EFFECTIVE', title: 'Newest Annuity effective' },
            { value: 'NEW-ANNUITY-PREMIUM', title: 'Newest Annuity initial premium' },
            { value: 'NEW-ANNUITY-NUMBER', title: 'Newest Annuity policy number' },
            { value: 'NEW-ANNUITY-AOR-FRST', title: 'Newest ANNUITY AOR first name' },
            { value: 'NEW-ANNUITY-AOR-LST', title: 'Newest ANNUITY AOR last name' },
            { value: 'NEW-ANNUITY-AOR-EML', title: 'Newest ANNUITY AOR email' },
            { value: 'NEW-ANNUITY-AOR-PHN', title: 'Newest ANNUITY AOR phone' },
          ],
        },
        {
          title: 'Any Policy',
          menu: [
            { value: 'POLICY', title: 'Policy List (lists all policies on account)' },
            { value: 'NEW-POL-COMPANY', title: 'Newest policy company name' },
            { value: 'NEW-POL-PLAN', title: 'Newest policy plan name' },
            { value: 'NEW-POL-EFFECTIVE', title: 'Newest policy effective' },
            { value: 'NEW-POL-PREMIUM', title: 'Newest policy premium' },
            { value: 'NEW-POL-SUBSIDY', title: 'Newest policy subsidy' },
            { value: 'NEW-POL-NUMBER', title: 'Newest policy number' },
            { value: 'NEW-POL-BENEFITS', title: 'Newest policy benefits' },
            { value: 'NEW-POL-AOR-FRST', title: 'Newest policy AOR first name' },
            { value: 'NEW-POL-AOR-LST', title: 'Newest policy AOR last name' },
            { value: 'NEW-POL-AOR-EML', title: 'Newest policy AOR email' },
            { value: 'NEW-POL-AOR-PHN', title: 'Newest policy AOR phone' },
          ],
        },
      ],
    },
    {
      title: 'PCP',
      menu: [
        { value: 'PCP-NAME', title: 'PCP name' },
        { value: 'PCP-FACILITY', title: 'PCP facility name' },
        { value: 'PCP-PHONE', title: 'PCP phone number' },
        { value: 'PCP-ADDR', title: 'PCP address' },
      ],
    },
    {
      title: 'Agent',
      menu: [
        { value: 'SIGNATURE', title: 'Letter Signature' },
        { value: 'SIG-EMAIL', title: 'Email Signature' },
        { value: 'SIG-IMG', title: 'Image Signature' },
        { value: 'LOGO', title: 'Company Logo' },
        { value: 'MY-NAME-FIRST', title: 'My first name' },
        { value: 'MY-NAME-LAST', title: 'My last name' },
        { value: 'MY-COMPANY-NAME', title: 'My company name' },
        { value: 'MY-EMAIL', title: 'My email address' },
        { value: 'MY-PHONE', title: 'My phone number' },
        { value: 'UNSUBSCRIBE', title: 'Unsubscribe' },
      ],
    },
    {
      title: 'Client Portal',
      menu:
        portalMergeList.length > 0
          ? portalMergeList
          : [{ value: 'PORTAL', title: 'Portal Invite' }],
    },
    {
      title: 'Client Portal - QR Codes',
      menu:
        portalQRMergeList.length > 0
          ? portalQRMergeList.map((t) => ({
              value: t.value.replace('CP-', 'CPQR-'),
              title: t.title.replace('Portal -', 'Portal QR Code -'),
            }))
          : [{ value: 'QRCODE', title: 'Portal Invite (QR code)' }],
    },
  ];
}
